import { useRef, useState } from 'react'
import { Icon, toast } from '@opallabs/lapidary'
import { TextEditorAsset, useTextEditor } from '../../../TextEditor'
import {
  ControlButton,
  ControlButtonGroup,
  AssetLibraryControlButton,
  HighlightControlButton,
  TableControlButton,
  UploadControlButton,
  TextColorControlButton,
} from '../../../TextEditor/components/ControlBar'
import { ExportFailedToast } from '../../../TextEditor/toasts'
import { Alignment } from '../../../TextEditor/utils'
import { useFullControlBarStyles } from '../../hooks'
import { exportEditorDocx } from '../../utils'

type ButtonGroupProps = {
  isInsidePopover?: boolean
}

type AlignButtonsProps = ButtonGroupProps & {
  activeTextAlign: Alignment
}

type TableButtonsProps = ButtonGroupProps & {
  isTableActive: boolean
}

type ExportButtonsProps = ButtonGroupProps & {
  documentName?: string
}

type AttachmentButtonsProps = ButtonGroupProps & {
  onPickAssets?: () => Promise<TextEditorAsset[]>
  hyperlinkManager: { toggle: () => void; isOpen: boolean }
}

export const HeadingButtons = ({
  isInsidePopover = false,
}: ButtonGroupProps) => {
  const styleOverrides = useFullControlBarStyles(isInsidePopover)

  return (
    <ControlButtonGroup styleOverrides={styleOverrides}>
      <ControlButton
        onClick={(chain) => chain.setParagraph()}
        isActive={['paragraph']}
        label="Paragraph"
        icon={<Icon glyph="gem-paragraph" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleHeading({ level: 1 })}
        isActive={['heading', { level: 1 }]}
        label="Heading 1"
        icon={<Icon glyph="gem-h1" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleHeading({ level: 2 })}
        isActive={['heading', { level: 2 }]}
        label="Heading 2"
        icon={<Icon glyph="gem-h2" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleHeading({ level: 3 })}
        isActive={['heading', { level: 3 }]}
        label="Heading 3"
        icon={<Icon glyph="gem-h3" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleHeading({ level: 4 })}
        isActive={['heading', { level: 4 }]}
        label="Heading 4"
        icon={<Icon glyph="gem-h4" ariaHidden />}
      />
    </ControlButtonGroup>
  )
}

export const FormattingButtons = ({
  isInsidePopover = false,
}: ButtonGroupProps) => {
  const styleOverrides = useFullControlBarStyles(isInsidePopover)

  return (
    <ControlButtonGroup styleOverrides={styleOverrides}>
      <TextColorControlButton />
      <HighlightControlButton />
      <ControlButton
        onClick={(chain) => chain.toggleBold()}
        isActive={['bold']}
        label="Bold"
        icon={<Icon glyph="gem-bold" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleItalic()}
        isActive={['italic']}
        label="Italic"
        icon={<Icon glyph="gem-italic" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleUnderline()}
        isActive={['underline']}
        label="Underline"
        icon={<Icon glyph="gem-underline" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleStrike()}
        isActive={['strike']}
        label="Strikethrough"
        icon={<Icon glyph="gem-strikethrough" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleSuperscript()}
        isActive={['superscript']}
        label="Superscript"
        icon={<Icon glyph="gem-superscript" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleSubscript()}
        isActive={['subscript']}
        label="Subscript"
        icon={<Icon glyph="gem-subscript" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleBlockquote()}
        isActive={['blockquote']}
        label="Blockquote"
        icon={<Icon glyph="gem-quote" ariaHidden />}
      />
      <ControlButton
        label="Separator"
        icon={<span aria-hidden="true">—</span>}
        isActive={['horizontalRule']}
        onClick={(chain) =>
          // focus is needed afterwards to keep
          // the cursor visible in the editor
          chain.setHorizontalRule().focus()
        }
      />
    </ControlButtonGroup>
  )
}

export const AlignButtons = ({
  activeTextAlign,
  isInsidePopover = false,
}: AlignButtonsProps) => {
  const styleOverrides = useFullControlBarStyles(isInsidePopover)

  return (
    <ControlButtonGroup styleOverrides={styleOverrides}>
      <ControlButton
        onClick={(chain) => chain.setTextAlign('left')}
        isActive={activeTextAlign === 'left'}
        label="Left Align"
        icon={<Icon glyph="gem-align-left" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.setTextAlign('center')}
        isActive={activeTextAlign === 'center'}
        label="Center Align"
        icon={<Icon glyph="gem-align-center" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.setTextAlign('right')}
        isActive={activeTextAlign === 'right'}
        label="Right Align"
        icon={<Icon glyph="gem-align-right" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.setTextAlign('justify')}
        isActive={activeTextAlign === 'justify'}
        label="Justify Align"
        icon={<Icon glyph="gem-align-justified" ariaHidden />}
      />
    </ControlButtonGroup>
  )
}

export const ListButtons = ({ isInsidePopover = false }: ButtonGroupProps) => {
  const styleOverrides = useFullControlBarStyles(isInsidePopover)

  return (
    <ControlButtonGroup styleOverrides={styleOverrides}>
      <ControlButton
        onClick={(chain) => chain.toggleBulletList()}
        isActive={['bulletList']}
        label="Bulleted List"
        icon={<Icon glyph="gem-bullet-list" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleOrderedList()}
        isActive={['orderedList']}
        label="Numbered List"
        icon={<Icon glyph="gem-numbered-list" ariaHidden />}
      />
      <ControlButton
        onClick={(chain) => chain.toggleTaskList()}
        isActive={['taskList']}
        label="Task List"
        icon={<Icon glyph="gem-add-checkbox" ariaHidden />}
      />
    </ControlButtonGroup>
  )
}

export const TableButtons = ({
  isInsidePopover = false,
  isTableActive,
}: TableButtonsProps) => {
  const styleOverrides = useFullControlBarStyles(isInsidePopover)

  return (
    <ControlButtonGroup styleOverrides={styleOverrides}>
      <ControlButton
        onClick={(chain) => {
          // only add table if one doesn't already exist at cursor
          if (!isTableActive) {
            chain.focus().insertTable({ rows: 2, cols: 2 }).run()
          }
        }}
        isActive={['table']}
        label="Table"
        icon={<Icon glyph="gem-add-table" ariaHidden />}
      />
      {isTableActive && <TableControlButton />}
    </ControlButtonGroup>
  )
}

export const ExportButtons = ({
  isInsidePopover = false,
  documentName,
}: ExportButtonsProps) => {
  const styleOverrides = useFullControlBarStyles(isInsidePopover)
  const { editor } = useTextEditor()
  const [isExporting, setIsExporting] = useState(false)
  // A second click can arrive before the re-render that sets `isExporting`.
  const exportingRef = useRef(false)

  const handleExport = async () => {
    if (exportingRef.current) return
    exportingRef.current = true
    setIsExporting(true)
    try {
      await exportEditorDocx(editor, documentName)
    } catch (error) {
      console.error('The DOCX export failed.', error)
      toast(ExportFailedToast, {
        type: 'error',
        closeOnClick: true,
        autoClose: 5000,
      })
    } finally {
      exportingRef.current = false
      setIsExporting(false)
    }
  }

  // `aria-disabled`, not `disabled`, so the button keeps keyboard focus.
  return (
    <ControlButtonGroup styleOverrides={styleOverrides}>
      <ControlButton
        onClick={handleExport}
        aria-disabled={isExporting}
        label="Download as Word Document"
        icon={<Icon glyph="gem-download" ariaHidden />}
      />
    </ControlButtonGroup>
  )
}

export const AttachmentButtons = ({
  isInsidePopover = false,
  onPickAssets,
  hyperlinkManager,
}: AttachmentButtonsProps) => {
  const styleOverrides = useFullControlBarStyles(isInsidePopover)

  return (
    <ControlButtonGroup styleOverrides={styleOverrides}>
      <UploadControlButton
        label="Upload File"
        icon={<Icon glyph="gem-upload-file-attachment" ariaHidden />}
      />
      {typeof onPickAssets === 'function' && (
        <AssetLibraryControlButton
          label="Open Asset Library"
          icon={<Icon glyph="gem-photo-attachment" ariaHidden />}
          onPickAssets={onPickAssets}
        />
      )}
      <ControlButton
        onClick={() => hyperlinkManager.toggle()}
        disabled={hyperlinkManager.isOpen}
        label="Add Link"
        icon={<Icon glyph="gem-add-link" ariaHidden />}
      />
    </ControlButtonGroup>
  )
}
